<template>
  <main class="k2-page" :class="{ 'k2-page--zh': isZh }" :style="{ '--k2-tablet-scale': tabletScale }">
    <nav class="k2-subnav" :aria-label="copy.navAria">
      <div class="k2-subnav__inner">
        <a class="k2-subnav__brand" href="#k2-hero">Anovator K2</a>
        <div class="k2-subnav__links">
          <a v-for="item in navItems" :key="item.id" :href="`#${item.id}`">{{ item.label }}</a>
        </div>
        <a class="k2-subnav__cta" href="#contact-modal" @click.prevent="openContactModal">{{ copy.requestDemo }}</a>
      </div>
    </nav>

    <section id="k2-hero" class="k2-section k2-hero">
      <div class="k2-canvas">
        <h1>ANOVATOR <em>K2</em></h1>
        <p class="k2-hero__lead">{{ copy.heroTagline }}</p>
        <img class="k2-hero__wordmark" src="/static/images/product/k2/hero-wordmark.svg" alt="" aria-hidden="true">
        <img class="k2-hero__image" src="/static/images/product/k2/hero.png" alt="ANOVATOR K2 kids body composition analyzer" fetchpriority="high">
        <img class="k2-hero__detail" src="/static/images/product/k2/hero-detail.png" alt="" aria-hidden="true">
      </div>
    </section>

    <section id="k2-functions" class="k2-section k2-overview">
      <div class="k2-canvas">
        <h2>{{ copy.overviewTitle }}</h2>
        <div v-if="isZh" class="k2-overview__cn">
          <span v-for="item in cnOverviewItems" :key="item">{{ item }}</span>
        </div>
        <template v-else>
        <div class="k2-overview__desktop">
          <span v-for="item in overviewItems" :key="item.title">{{ item.title }}</span>
        </div>
        <div class="k2-overview__mobile">
          <article v-for="item in overviewItems.slice(0, 4)" :key="item.title">
            <h3>{{ item.title }}</h3>
            <p>{{ item.description }}</p>
          </article>
        </div>
        </template>
      </div>
    </section>

    <section id="k2-composition" class="k2-section k2-composition">
      <div class="k2-canvas">
        <header class="k2-section-heading">
          <h2>{{ copy.compositionTitle }}</h2>
          <p>{{ copy.compositionLead }}</p>
        </header>
        <div class="k2-composition__visual" aria-hidden="true">
          <img class="k2-composition__rings" src="/static/images/product/k2/composition.svg" alt="">
          <div v-for="bubble in visibleCompositionBubbles" :key="bubble.label" class="k2-bubble" :class="`k2-bubble--${bubble.key}`">
            <img :src="bubble.image" alt="">
            <span>{{ bubble.label }}</span>
          </div>
        </div>
      </div>
    </section>

    <section id="k2-growth" class="k2-section k2-growth">
      <div class="k2-canvas k2-centered">
        <h2>{{ copy.growthTitle }}</h2>
        <p class="k2-section-lead">{{ copy.growthLead }}</p>
        <img src="/static/images/product/k2/growth.png" alt="Children's height growth trend chart">
      </div>
    </section>

    <section id="k2-height" class="k2-section k2-height">
      <div class="k2-canvas k2-centered">
        <h2>{{ copy.heightTitle }}</h2>
        <p class="k2-section-lead">{{ copy.heightLead }}</p>
        <img src="/static/images/product/k2/height.png" alt="K2 ultrasonic height measurement sensor">
      </div>
    </section>

    <section id="k2-assessment" class="k2-section k2-balance-agility">
      <div class="k2-canvas">
        <article class="k2-balance">
          <h2>{{ copy.balanceTitle }}</h2>
          <p>{{ copy.balanceLead }}</p>
          <img src="/static/images/product/k2/balance.png" alt="Child taking the K2 balance assessment">
        </article>
        <article class="k2-agility">
          <img src="/static/images/product/k2/agility.png" alt="Child taking the K2 agility assessment">
          <div>
            <h2>{{ copy.agilityTitle }}</h2>
            <p>{{ copy.agilityLead }}</p>
          </div>
        </article>
      </div>
    </section>

    <section id="k2-dimensions" class="k2-section k2-dimensions">
      <div class="k2-canvas">
        <h2>{{ copy.dimensionsTitle }}</h2>
        <div class="k2-dimensions__visual">
          <img class="k2-dimensions__body" src="/static/images/product/k2/dimensions.png" alt="Body dimension measurement model">
          <span v-for="(item, index) in visibleDimensionLabels" :key="item" :class="`k2-dimension-label k2-dimension-label--${index + 1}`">{{ item }}</span>
        </div>
      </div>
    </section>

    <section id="k2-posture" class="k2-section k2-posture">
      <div class="k2-canvas k2-centered">
        <h2>{{ copy.postureTitle }}</h2>
        <p class="k2-section-lead">{{ copy.postureLead }}</p>
        <div class="k2-posture__grid">
          <figure v-for="item in postureItems" :key="item.label">
            <img :src="item.image" :alt="item.label">
          </figure>
        </div>
      </div>
    </section>

    <section id="k2-vision" class="k2-section k2-vision">
      <div class="k2-canvas k2-centered">
        <h2>{{ copy.visionTitle }}</h2>
        <img src="/static/images/product/k2/vision.png" alt="K2 eyesight detection program">
      </div>
    </section>

    <section id="k2-vital-capacity" class="k2-section k2-vital-capacity">
      <div class="k2-canvas k2-centered">
        <h2>{{ copy.vitalTitle }}</h2>
        <p class="k2-section-lead">{{ copy.vitalLead }}</p>
        <img src="/static/images/product/k2/vital-capacity.png" alt="Child performing a vital capacity assessment">
      </div>
    </section>

    <section id="k2-display" class="k2-section k2-display">
      <div class="k2-canvas k2-centered">
        <h2>{{ copy.displayTitle }}</h2>
        <div class="k2-display__metrics">
          <p><span>{{ copy.displaySize }}</span><strong>{{ isZh ? '32英寸' : '32 INCH' }}</strong></p>
          <p><span>{{ copy.displayResolution }}</span><strong>1080P</strong></p>
        </div>
        <img src="/static/images/product/k2/display.png" alt="K2 ultra-HD LCD display">
      </div>
    </section>

    <section id="k2-ai" class="k2-section k2-ai-vision">
      <div class="k2-canvas">
        <div class="k2-ai-vision__title"><strong>AI</strong><span><em>1080P</em> {{ copy.aiVision }}</span></div>
        <img src="/static/images/product/k2/ai-vision.png" alt="K2 AI smart vision lens">
      </div>
    </section>

    <section id="k2-management" class="k2-section k2-management">
      <div class="k2-canvas k2-centered">
        <p class="k2-management__kicker">{{ copy.managementKicker }}</p>
        <h2>{{ copy.managementTitle }}</h2>
        <img src="/static/images/product/k2/management.png" alt="ANOVATOR intelligent management dashboard">
        <div class="k2-management__cards">
          <article v-for="item in visibleManagementItems" :key="item.title">
            <h3>{{ item.title }}</h3>
            <p>{{ item.description }}</p>
          </article>
        </div>
      </div>
    </section>

    <section id="k2-compare" class="k2-section k2-compare-heading" :class="{ 'p-series-compare': isZh }">
      <header v-if="isZh" class="p5-comparison__heading"><h2>看看哪款体测仪适合你。</h2></header>
      <div v-else class="k2-canvas"><h2>{{ copy.compareTitle }}</h2></div>
    </section>

    <section class="k2-section k2-comparison" :class="{ 'p-series-compare': isZh }">
      <div v-if="isZh" class="p5-comparison__panel k2-comparison__panel--cn" aria-label="K1 与 K2 产品参数和功能对比">
        <article v-for="product in cnComparisonProducts" :key="product.key" class="p5-product-card">
          <img :src="product.image" :alt="`ANOVATOR ${product.name}`">
          <div class="p5-product-card__model"><h3>{{ product.name }}</h3></div>
          <ul class="p5-product-card__functions">
            <li v-for="row in cnCompareRows.slice(4)" :key="row.label" :class="{ 'is-unsupported': row[product.key] === '—' }">
              <span>{{ row.label }} {{ row[product.key] }}</span>
            </li>
          </ul>
          <dl>
            <div v-for="row in cnCompareRows.slice(0, 4)" :key="row.label" class="k2-cn-compare-row">
              <dt>{{ row.label }}</dt><dd>{{ row[product.key] }}</dd>
            </div>
          </dl>
        </article>
      </div>
      <div v-else class="k2-comparison__panel">
        <article class="k2-compare-product">
          <img class="k2-compare-product__image" src="/static/images/product/k2/compare-p5.png" alt="ANOVATOR P5">
          <h3>P5</h3>
          <span class="k2-compare-switch" aria-hidden="true"><img src="/static/images/product/k2/compare-switch.svg" alt=""></span>
          <div class="k2-compare-data">
            <p v-for="item in compareP5.features" :key="item">{{ item }}</p>
            <hr>
            <strong>{{ compareP5.display }}</strong><span>Display</span>
            <hr>
            <strong class="k2-frequencies">{{ compareP5.frequencies }}</strong><span>Frequencies</span>
            <hr>
            <strong>{{ compareP5.distance }}</strong><span>Working Distance</span>
          </div>
        </article>
        <article class="k2-compare-product">
          <picture>
            <source media="(max-width: 1023px)" srcset="/static/images/product/k2/compare-m9.png">
            <img class="k2-compare-product__image" src="/static/images/product/k2/compare-s1.png" alt="ANOVATOR comparison model">
          </picture>
          <h3><span class="k2-compare-name--desktop">S1</span><span class="k2-compare-name--mobile">M9</span></h3>
          <div class="k2-compare-data">
            <p v-for="item in compareM9.features" :key="item">{{ item }}</p>
            <hr>
            <strong>{{ compareM9.display }}</strong><span>Display</span>
            <hr>
            <strong class="k2-frequencies">{{ compareM9.frequencies }}</strong><span>Frequencies</span>
            <hr>
            <strong>{{ compareM9.distance }}</strong><span>Working Distance</span>
          </div>
        </article>
      </div>
    </section>

    <section class="k2-disclaimer">
      <div class="k2-disclaimer__inner">
        <p>{{ copy.disclaimer }}</p>
      </div>
    </section>
  </main>
</template>

<script>
const { requestContactModal } = require('@/utils/contact-modal.cjs')
const { siteLanguageMixin } = require('@/utils/site-language.js')

export default {
  name: 'K2',
  mixins: [siteLanguageMixin],
  data() {
    return {
      viewportWidth: typeof window === 'undefined' ? 1440 : window.innerWidth,
      cnCommonFunctions: ['人体成分分析', '体态评估', '身体围度评估', '平衡评估', '敏捷评估', '成长曲线', '成长评分', '成年身高预测'],
      cnK2Functions: ['肺活量测量', '视力检测', '一分钟跳绳测试'],
      enNavItems: [
        { id: 'k2-functions', label: 'New Upgrade' },
        { id: 'k2-composition', label: 'Core Functions' },
        { id: 'k2-display', label: 'Exterior Design' },
        { id: 'k2-management', label: 'Support Services' },
        { id: 'k2-compare', label: 'Spec Compare' }
      ],
      overviewItems: [
        { title: 'Body Composition Analysis', description: 'With 4-electrode bioelectrical impedance analysis, it gently and accurately assesses key health metrics like body fat percentage, muscle mass, and bone density.' },
        { title: 'Body Dimensions and Posture', description: 'Gently and accurately measures key dimensions like height and circumferences, offering a clear visual representation of body shape changes.' },
        { title: 'Balance Assessment', description: "Using high-precision sensors, it comprehensively records subtle body changes and accurately captures balance status, helping track children's physical development with care." },
        { title: 'Agility and Reaction Assessment', description: "Intelligently detects trigger moments, offering guidance for improving children's reaction ability training." },
        { title: 'Vision Assessment', description: '' },
        { title: 'Vital Capacity Assessment', description: '' }
      ],
      compositionBubbles: [
        { key: 'bone', label: 'Bone', image: '/static/images/product/k2/composition-bone.svg' },
        { key: 'visceral', label: 'Visceral fat', image: '/static/images/product/k2/composition-visceral.svg' },
        { key: 'muscle', label: 'Muscle', image: '/static/images/product/k2/composition-muscle.svg' },
        { key: 'protein', label: 'Protein', image: '/static/images/product/k2/composition-protein.svg' },
        { key: 'bmr', label: 'BMR', image: '/static/images/product/k2/composition-bmr.svg' },
        { key: 'bmi', label: 'BMI', image: '/static/images/product/k2/composition-bmi.svg' },
        { key: 'weight', label: 'Weight', image: '/static/images/product/k2/composition-weight.svg' },
        { key: 'water', label: 'Water', image: '/static/images/product/k2/composition-water.svg' },
        { key: 'salts', label: 'Inorganic salts', image: '/static/images/product/k2/composition-salts.svg' },
        { key: 'fat', label: 'Subcutaneous fat', image: '/static/images/product/k2/composition-fat.svg' }
      ],
      dimensionLabels: ['Hip size', 'Shoulder width', 'Arm span', 'Height', 'WHR', 'Waist size', 'Thigh size', 'Chest size'],
      postureItems: [
        { label: 'Uneven Shoulders', image: '/static/images/product/k2/posture.png' },
        { label: 'Hunched Back', image: '/static/images/product/k2/posture-2.png' },
        { label: 'Bow Legs', image: '/static/images/product/k2/posture-3.png' },
        { label: 'Anterior Pelvic Tilt', image: '/static/images/product/k2/posture-4.png' },
        { label: 'Scoliosis', image: '/static/images/product/k2/posture-5.png' },
        { label: 'Knee Hyperextension', image: '/static/images/product/k2/posture-6.png' },
        { label: 'Head Tilt', image: '/static/images/product/k2/posture-7.png' },
        { label: 'Forward Head Posture', image: '/static/images/product/k2/posture-8.png' }
      ],
      managementItems: [
        { title: 'Software Platform: End-to-End Traceability', description: 'You can access information such as a single device\'s manufacturing date, installation location, and ownership details at any time. This gives the device a “memory,” ensuring that every step of your traceability process is fully traceable.' },
        { title: 'Cloud-Based Configuration, Instant Deployment', description: 'For bulk orders from large-scale chain organizations, unified configuration and management can be performed via a cloud platform, significantly enhancing deployment and operations efficiency.' }
      ],
      compareP5: {
        features: ['Body Composition Analysis', 'Posture & Body Dimension Analysis', 'Exercise Safety Assessment', 'Cardiorespiratory Assessment', 'Agility & Reaction Assessment', 'Balance Assessment', 'Lower-Body Power Assessment', 'Upper-Body Power Assessment', 'Grip Strength Assessment', 'Lung Capacity Assessment', '-', '-'],
        display: '27-inch 4K Fully Laminated Display', frequencies: '20kHz\n100kHz\n250kHz\n-\n-\n-\n-\n-', distance: '1m'
      },
      compareM9: {
        features: ['Body Composition Analysis', '3D Posture & Body Dimension Analysis', 'Exercise Safety Assessment', 'Cardiorespiratory Assessment', '-', '-', '-', '-', 'Grip Strength Assessment', 'Lung Capacity Assessment', 'Vision Assessment', 'Color Blindness Assessment'],
        display: '43-inch 4K Fully Laminated Display', frequencies: '5kHz\n50kHz\n100kHz\n250kHz\n500kHz\n1000kHz\n2000kHz\n3000kHz', distance: '1.5m'
      }
    }
  },
  computed: {
    cnOverviewItems() { return ['人体成分分析', '体态与围度测量', '平衡能力评估', '敏捷评估', '视力检测', '肺活量测量'] },
    cnComparisonProducts() {
      return [
        { key: 'k1', name: 'K1', image: '/static/images/product/k2/compare-k1-product.png' },
        { key: 'k2', name: 'K2', image: '/static/images/product/k2/compare-k2-product.png' }
      ]
    },
    cnCompareRows() {
      return [
        { label: '检测频率', k1: '20kHz、200kHz', k2: '20kHz、200kHz' },
        { label: '显示屏', k1: '21.5英寸高清屏', k2: '32英寸高清屏' },
        { label: '工作距离', k1: '1.5m', k2: '1.5m' },
        { label: '电极数量', k1: '4电极', k2: '4电极' },
        ...this.cnCommonFunctions.map(label => ({ label, k1: '✓', k2: '✓' })),
        ...this.cnK2Functions.map(label => ({ label, k1: '—', k2: '✓' }))
      ]
    },
    visibleCompositionBubbles() {
      const labels = ['骨骼', '内脏脂肪', '肌肉', '蛋白质', '基础代谢率', '身体质量指数', '体重', '身体水分', '无机盐', '皮下脂肪']
      return this.isZh ? this.compositionBubbles.map((item, index) => ({ ...item, label: labels[index] })) : this.compositionBubbles
    },
    visibleDimensionLabels() { return this.isZh ? ['臀围', '肩宽', '臂展', '身高', '腰臀比', '腰围', '大腿围', '胸围'] : this.dimensionLabels },
    visibleManagementItems() {
      return this.isZh ? [
        { title: '软件平台：全流程追溯', description: '可随时查询单台设备的生产日期、安装位置及归属信息，记录设备信息，让追溯流程的每一步都有据可查。' },
        { title: '云端配置，即时部署', description: '针对大型连锁机构的批量订单，可通过云端平台统一配置和管理，提升部署及运维效率。' }
      ] : this.managementItems
    },
    tabletScale() { return Math.min(1, this.viewportWidth / 1440) },
    isZh() { return this.currentLanguage === 'ZH' },
    copy() {
      return this.isZh ? {
        navAria: 'K2 产品导航', requestDemo: '申请演示', heroTagline: '精准读懂成长数据，护航儿童健康未来',
        overviewTitle: '儿童专业智能体测仪\n科学护成长', compositionTitle: '自研技术 真实测量\n人体成分分析', compositionLead: '基于真实节段分析，我们拒绝简单公式计算，而是通过精准感应与智能算法，分区测量不同部位的生理数据，提供真实、准确的健康评估，帮助用户更科学管理身体状态。',
        growthTitle: '大数据成长趋势分析\n未来可期', growthLead: '基于60万+例儿童实例数据训练，并根据儿童性别，自动动态对比同龄同性别儿童百分位线，结合多项检测数据，提供成年身高预测及评估。',
        heightTitle: '超声波身高测量\n高效准确', heightLead: '超声波高速探测，瞬间测量身高，助您实现快速、精准的智能检测体验，确保数据真实准确。',
        balanceTitle: '平衡检测\n成就均衡成长', balanceLead: '内置高精度传感器，根据综合计算，评估儿童前庭功能。', agilityTitle: '敏捷能力测试\n瞬间响应 智动评估', agilityLead: '通过互动游戏进行敏捷反应测试，直观评估感统训练成果。', dimensionsTitle: '多维解析\n定义成长曲线', postureTitle: '早预警 早干预\n高效全面的体态评估', postureLead: '采用高精度AI摄像头，结合云端人体骨骼点大数据对比分析，评估人体姿态风险，提早发现儿童体态问题，以便尽早干预。', visionTitle: '抗干扰裸眼视力\n检测方案', vitalTitle: '肺活量评估系统', vitalLead: '肺活量是评估儿童和青少年心肺发育的核心指标。K2肺活量评估系统专为青少年体质打造，配备高灵敏度压力感应元件，将传统肺功能测试升级为可视化“数字呼吸挑战”，提升儿童配合度，并为精准体质改善提供可靠数据支持。', displayTitle: '高清液晶屏\n守护视界', displaySize: '屏幕尺寸', displayResolution: '分辨率', aiVision: '智能视觉镜头', managementKicker: '系统', managementTitle: '源诺医疗智能管理后台', compareTitle: '看看哪款体测仪\n适合你。', disclaimer: '特别声明：以上页面中的产品图片及屏幕内容仅作示意，产品实际外观与显示内容可能存在差异，一切请以实物为准。以上数据为特定测试条件下的理论值，实际使用效果可能有所不同。'
      } : {
        navAria: 'K2 product navigation', requestDemo: 'Request demo', heroTagline: "Kids' Body Composition Analyzer", overviewTitle: 'Fitness Tester Designed Specifically for Children\nIntelligently Building a Healthy Childhood', compositionTitle: 'Proprietary Technology for True-to-Life Body Composition Analysis.', compositionLead: 'Based on real segmental analysis, we reject simplistic formula-based calculations. Instead, through precise sensing and intelligent algorithms, it measures physiological data across different body zones, delivering true and accurate health assessments to help users manage their physical condition more scientifically.', growthTitle: 'Big-Data Growth Trend Analysis\nA Promising Future Ahead', growthLead: "Leveraging advanced fitness testing technology and big-data analysis, it accurately predicts children's height growth trends. By monitoring growth curves in real time, it provides tailored scientific development recommendations for every child, empowering parents with early intervention to safeguard their children's healthy growth journey.", heightTitle: 'Ultrasonic Height Measurement\nEfficient and Accurate', heightLead: 'Ultrasonic high-speed detection measures height in an instant, delivering a fast and accurate intelligent testing experience with reliable, true-to-life data.', balanceTitle: 'Balance Assessment\nBalanced Growth', balanceLead: "Built-in high-precision sensors evaluate children's vestibular function through comprehensive calculation.", agilityTitle: 'Agility Assessment – Instant Response, Smart Evaluation', agilityLead: 'Conduct agility-reaction tests via interactive games to intuitively evaluate the outcomes of sensory integration training.', dimensionsTitle: 'Multi-Dimensional Analysis – Defining Your Growth Curve', postureTitle: 'Early Warning, Early Intervention – Efficient & Comprehensive Posture Assessment', postureLead: 'Utilizing high-precision AI-powered cameras and cloud-based comparative analysis of extensive human skeletal data, our system evaluates postural risks to detect body alignment issues in both adults and children at an early stage, enabling timely intervention.', visionTitle: 'Anti-Interfering Naked-Eye Eyesight\nDetection Program', vitalTitle: 'Vital Capacity Assessment System', vitalLead: "Vital capacity serves as a core indicator for evaluating the cardiopulmonary development of children and teenagers. The K2 Vital-Capacity Assessment System is developed specifically for young people's physical fitness. Equipped with high-sensitivity pressure-sensing components, it upgrades conventional pulmonary-function testing into a visualized “digital breathing challenge” to boost children's cooperation. It also supplies reliable data support for targeted physical-improvement programmes.", displayTitle: 'Ultra-HD LCD Display\nProtect Your Vision', displaySize: 'Size', displayResolution: 'Resolution', aiVision: 'Smart Vision Lens', managementKicker: 'System', managementTitle: 'ANOVATOR Intelligent Management Dashboard', compareTitle: 'Find the ANOVATOR That Fits Your Needs.', disclaimer: 'Disclaimer: The product images and screen content shown on the page above are for illustrative purposes only. The actual product appearance and displayed content may vary; please refer to the actual product.'
      }
    },
    navItems() { return this.isZh ? [{ id: 'k2-functions', label: '新品升级' }, { id: 'k2-composition', label: '核心功能' }, { id: 'k2-display', label: '外观设计' }, { id: 'k2-management', label: '支持服务' }, { id: 'k2-compare', label: '规格比较' }] : this.enNavItems }
  },
  mounted() { window.addEventListener('resize', this.syncViewportWidth) },
  beforeUnmount() { window.removeEventListener('resize', this.syncViewportWidth) },
  methods: {
    syncViewportWidth() { this.viewportWidth = window.innerWidth },
    openContactModal(event) {
      requestContactModal(event?.currentTarget)
    }
  }
}
</script>

<style scoped>
.k2-page { --k2-desktop-canvas: 1440px; --k2-content-width: 1296px; --k2-mobile-gutter: 16px; position: relative; color: #000; background: #fff; font-family: 'MiSans-Regular', Arial, sans-serif; }
.k2-page *, .k2-page *::before, .k2-page *::after { box-sizing: border-box; }
.k2-page :is(h1, h2, h3, p, figure) { margin: 0; }
.k2-section { position: relative; width: 100%; overflow: hidden; }
.k2-canvas { position: relative; left: 50%; width: var(--k2-desktop-canvas); height: 100%; transform: translateX(-50%); }
.k2-centered { text-align: center; }
.k2-section h2 { font-family: 'MiSans-Bold', Arial, sans-serif; font-size: 40px; font-weight: 700; line-height: 1.325; text-transform: uppercase; white-space: pre-line; }
.k2-section-lead { color: rgba(0, 0, 0, .6); font-size: 22px; line-height: 1.32; }

.k2-subnav { position: absolute; top: var(--global-header-height, 64px); left: 0; z-index: 900; width: 100%; height: 44px; color: #fff; background: #191b1d; }
.k2-subnav__inner { display: flex; align-items: center; width: min(100%, var(--k2-content-width)); height: 44px; margin: 0 auto; }
.k2-subnav a { color: inherit; font-size: 12px; line-height: 16px; text-decoration: none; }
.k2-subnav__brand { text-transform: uppercase; }
.k2-subnav__links { display: flex; gap: 38px; margin-left: auto; color: #cfcfcf; }
.k2-subnav__cta { display: flex; align-items: center; justify-content: center; width: 95px; height: 19px; margin-left: 38px; border-radius: 67px; color: #000 !important; background: #fff; font-size: 10px !important; }

.k2-hero { height: 770px; background: linear-gradient(180deg, #ffe1ca 0%, #fff 100%); }
.k2-hero h1 { position: absolute; top: 148px; left: 50%; z-index: 4; transform: translateX(-50%); font-family: 'MiSans-Heavy', Arial, sans-serif; font-size: 30px; line-height: 40px; white-space: nowrap; }
.k2-hero h1 em { color: #ff6f00; font-style: normal; }
.k2-hero__lead { position: absolute; top: 196px; left: 50%; z-index: 4; transform: translateX(-50%); color: rgba(0,0,0,.6); font-size: 16px; line-height: 21px; white-space: nowrap; }
.k2-hero__wordmark { position: absolute; top: 308px; left: 507px; width: 473px; height: 314px; }
.k2-hero__image { position: absolute; top: 188px; left: 421px; z-index: 2; width: 598px; height: 598px; object-fit: cover; }
.k2-hero__detail { position: absolute; top: 679px; left: 590px; z-index: 3; width: 39px; height: 38px; }

.k2-overview { height: 522px; background: #fff; }
.k2-overview h2 { position: absolute; top: 80px; left: 123px; width: 1194px; text-align: center; }
.k2-overview__desktop span { position: absolute; display: grid; place-items: center; height: 40px; padding: 0 49px; border-radius: 66px; background: #f5f5f7; font-family: 'MiSans-Bold', Arial, sans-serif; font-size: 18px; white-space: nowrap; }
.k2-overview__desktop span:nth-child(1) { top: 266px; left: 197px; }
.k2-overview__desktop span:nth-child(2) { top: 266px; left: 566px; }
.k2-overview__desktop span:nth-child(3) { top: 266px; left: 957px; }
.k2-overview__desktop span:nth-child(4) { top: 346px; left: 193px; }
.k2-overview__desktop span:nth-child(5) { top: 346px; left: 606px; }
.k2-overview__desktop span:nth-child(6) { top: 346px; left: 898px; }
.k2-overview__mobile { display: none; }

.k2-composition { height: 1123px; background: #fff; }
.k2-section-heading { position: absolute; top: 80px; left: 72px; z-index: 3; width: 1296px; text-align: center; }
.k2-section-heading p { margin-top: 32px; color: rgba(0,0,0,.6); font-size: 22px; line-height: 29px; }
.k2-composition__visual { position: absolute; inset: 10px 72px auto; width: 1296px; height: 1296px; }
.k2-composition__rings { width: 100%; height: 100%; }
.k2-bubble { position: absolute; display: grid; place-items: center; color: #fff; font-family: 'MiSans-Bold', Arial, sans-serif; text-align: center; text-transform: uppercase; }
.k2-bubble img { position: absolute; inset: 0; width: 100%; height: 100%; }
.k2-bubble span { position: relative; z-index: 1; font-size: clamp(7px, 1.45vw, 24px); }
.k2-bubble--bone { top: 146px; left: 310px; width: 279px; height: 279px; }
.k2-bubble--visceral { top: 853px; left: 183px; width: 159px; height: 159px; }
.k2-bubble--muscle { top: 536px; left: 264px; width: 216px; height: 216px; }
.k2-bubble--protein { top: 861px; left: 603px; width: 115px; height: 115px; }
.k2-bubble--bmr { top: 767px; left: 752px; width: 77px; height: 77px; }
.k2-bubble--bmi { top: 394px; left: 580px; width: 159px; height: 159px; }
.k2-bubble--weight { top: 351px; left: 867px; width: 113px; height: 113px; }
.k2-bubble--water { top: 673px; left: 913px; width: 353px; height: 353px; }
.k2-bubble--salts { top: 551px; left: 1173px; width: 61px; height: 61px; }
.k2-bubble--fat { top: 479px; left: 60px; width: 93px; height: 93px; }

.k2-growth { height: 1111px; background: #fff; }
.k2-growth h2, .k2-height h2 { padding-top: 80px; }
.k2-growth .k2-section-lead, .k2-height .k2-section-lead { width: 966px; margin: 32px auto 0; }
.k2-growth img { width: 636px; height: 636px; margin-top: 55px; object-fit: contain; }

.k2-height { height: 1111px; background: #f5f5f7; }
.k2-height img { width: 1296px; height: 680px; margin-top: -3px; border-radius: 28px; object-fit: cover; }

.k2-balance-agility { height: 1536px; background: #fff; }
.k2-balance h2 { position: absolute; top: 351px; left: 72px; width: 501px; text-align: left; }
.k2-balance p { display: none; }
.k2-balance img { position: absolute; top: 72px; left: 728px; width: 663px; height: 663px; object-fit: contain; }
.k2-agility > img { position: absolute; top: 808px; left: 72px; width: 648px; height: 648px; object-fit: contain; }
.k2-agility > div { position: absolute; top: 1053px; left: 763px; width: 605px; }
.k2-agility h2 { text-align: left; }
.k2-agility p { display: none; }

.k2-dimensions { height: 1111px; margin-top: 9px; background: #fff; }
.k2-dimensions h2 { position: absolute; top: 80px; left: 237px; width: 966px; text-align: center; }
.k2-dimensions__visual { position: absolute; top: 246px; left: 72px; width: 1296px; height: 780px; border-radius: 50%; background: radial-gradient(circle, rgba(255,111,0,.07), transparent 62%); }
.k2-dimensions__body { position: absolute; top: 0; left: 50%; width: 250px; height: 701px; transform: translateX(-50%); object-fit: contain; }
.k2-dimension-label { position: absolute; display: grid; place-items: center; min-width: 110px; min-height: 110px; padding: 18px; border-radius: 50%; color: #fff; background: radial-gradient(circle at 35% 35%, #ffbf8c, #ff6f00 65%, rgba(255,111,0,.05)); font-family: 'MiSans-Bold', Arial, sans-serif; font-size: 18px; text-align: center; }
.k2-dimension-label--1 { top: 58px; left: 300px; }
.k2-dimension-label--2 { top: 210px; left: 88px; }
.k2-dimension-label--3 { top: 492px; left: 240px; }
.k2-dimension-label--4 { top: 20px; right: 260px; }
.k2-dimension-label--5 { top: 235px; right: 90px; }
.k2-dimension-label--6 { top: 445px; right: 230px; }
.k2-dimension-label--7 { top: 596px; right: 420px; }
.k2-dimension-label--8 { top: 365px; left: 420px; }

.k2-posture { height: 1241px; background: #fff; }
.k2-posture h2 { width: 1296px; margin: 0 auto; padding-top: 80px; }
.k2-posture .k2-section-lead { width: 966px; margin: 32px auto 0; }
.k2-posture__grid { display: grid; grid-template-columns: repeat(4, 306px); gap: 24px; width: 1296px; margin: 46px auto 0; }
.k2-posture__grid figure { width: 306px; height: 376px; overflow: hidden; border-radius: 28px; }
.k2-posture__grid img { width: 100%; height: 100%; object-fit: cover; }

.k2-vision { height: 1006px; background: #f5f5f7; }
.k2-vision h2 { padding-top: 80px; }
.k2-vision img { width: 1296px; height: 680px; margin-top: 33px; border-radius: 28px; object-fit: cover; }

.k2-vital-capacity { height: 1006px; background: #fff; }
.k2-vital-capacity h2 { padding-top: 80px; }
.k2-vital-capacity .k2-section-lead { width: 966px; margin: 32px auto 0; }
.k2-vital-capacity img { position: absolute; bottom: 0; left: 428px; width: 509px; height: 590px; object-fit: contain; }

.k2-display { height: 1144px; background: #fff; }
.k2-display h2 { padding-top: 80px; }
.k2-display__metrics { display: flex; justify-content: center; gap: 94px; margin-top: 18px; }
.k2-display__metrics p { display: flex; flex-direction: column; }
.k2-display__metrics span { color: rgba(0,0,0,.6); font-size: 16px; line-height: 21px; }
.k2-display__metrics strong { margin-top: 3px; font-family: 'MiSans-Bold', Arial, sans-serif; font-size: 28px; line-height: 37px; }
.k2-display img { width: 1296px; height: 680px; margin-top: 60px; border-radius: 28px; object-fit: cover; }

.k2-ai-vision { height: 770px; background: #fff; }
.k2-ai-vision__title { position: absolute; top: 80px; left: 74px; z-index: 2; display: flex; flex-direction: column; align-items: flex-start; text-transform: uppercase; }
.k2-ai-vision__title strong { color: #ff6f00; font-family: 'MiSans-Bold', Arial, sans-serif; font-size: 120px; line-height: 1; }
.k2-ai-vision__title span { margin-top: -6px; font-family: 'MiSans-Bold', Arial, sans-serif; font-size: 40px; }
.k2-ai-vision__title em { color: #ff6f00; font-style: normal; }
.k2-ai-vision img { position: absolute; top: 80px; left: 74px; width: 1294px; height: 921px; object-fit: cover; }

.k2-management { height: 1498px; background: #fff; }
.k2-management__kicker { padding-top: 140px; font-family: 'MiSans-Bold', Arial, sans-serif; font-size: 28px; line-height: 37px; }
.k2-management h2 { width: 960px; margin: 20px auto 0; }
.k2-management > .k2-canvas > img { width: 752px; height: 682px; margin-top: 65px; object-fit: contain; }
.k2-management__cards { display: grid; grid-template-columns: repeat(2, 385px); gap: 86px; width: 856px; margin: -22px auto 0; text-align: left; }
.k2-management__cards article { border-top: 1px solid rgba(0,0,0,.35); padding-top: 20px; }
.k2-management__cards h3 { min-height: 54px; font-family: 'MiSans-Bold', Arial, sans-serif; font-size: 20px; line-height: 27px; }
.k2-management__cards p { margin-top: 20px; color: rgba(0,0,0,.6); font-family: 'MiSans-Bold', Arial, sans-serif; font-size: 17px; line-height: 23px; }

.k2-compare-heading { height: 253px; background: #f5f5f7; }
.k2-compare-heading h2 { width: 885px; margin: 0 auto; padding-top: 100px; text-align: center; text-transform: none; }
.k2-comparison { height: 1787px; background: #f5f5f7; }
.k2-comparison__panel { position: relative; left: 50%; display: grid; grid-template-columns: repeat(2, 416px); justify-content: center; gap: 24px; width: 1296px; height: 1787px; overflow: hidden; transform: translateX(-50%); border-radius: 32px; background: #fff; }
.k2-compare-product { position: relative; padding-top: 0; text-align: center; }
.k2-compare-product__image { width: 416px; height: 416px; object-fit: cover; }
.k2-compare-product h3 { height: 69px; font-family: 'MiSans-Bold', Arial, sans-serif; font-size: 40px; line-height: 53px; }
.k2-compare-switch { position: absolute; top: 426px; left: calc(50% + 39px); width: 34px; height: 34px; }
.k2-compare-switch img { width: 100%; height: 100%; }
.k2-compare-data p { min-height: 37px; color: rgba(0,0,0,.5); font-size: 16px; line-height: 21px; }
.k2-compare-data hr { width: 416px; margin: 19px 0 39px; border: 0; border-top: 1px solid rgba(0,0,0,.12); }
.k2-compare-data strong, .k2-compare-data span { display: block; }
.k2-compare-data strong { min-height: 74px; font-family: 'MiSans-Bold', Arial, sans-serif; font-size: 28px; line-height: 37px; white-space: pre-line; }
.k2-compare-data span { color: rgba(0,0,0,.5); font-size: 16px; line-height: 21px; }
.k2-compare-data .k2-frequencies { min-height: 296px; }
.k2-compare-name--mobile { display: none; }
.k2-disclaimer { height: 236px; margin-top: 80px; background: #fff; }
.k2-disclaimer__inner { position: relative; left: 50%; width: 1296px; padding-top: 28px; transform: translateX(-50%); border-top: 1px solid rgba(0,0,0,.15); }
.k2-disclaimer p { color: rgba(0,0,0,.35); font-size: 11px; line-height: 16px; }

@media (min-width: 2000px) {
  .k2-canvas { width: var(--k2-desktop-canvas); }
  .k2-comparison__panel, .k2-disclaimer__inner { width: var(--k2-content-width); }
}

@media (max-width: 1023px) {
  .k2-page--zh .k2-overview { height: 410px; }
  .k2-page { --k2-mobile-shell: min(100vw, 432px); --k2-mobile-content: calc(var(--k2-mobile-shell) - 32px); padding-top: var(--global-header-height, 50px); background: #fff; }
  .k2-canvas { left: auto; width: var(--k2-mobile-shell); margin: 0 auto; transform: none; }
  .k2-section h2 { font-size: 40px; line-height: 53px; }
  .k2-section-lead { width: var(--k2-mobile-content) !important; font-size: 14px; line-height: 19px; text-align: left; }
  .k2-subnav { top: var(--global-header-height, 50px); height: 50px; }
  .k2-subnav__inner { width: min(100vw, 432px); height: 50px; padding: 0 var(--k2-mobile-gutter); }
  .k2-subnav__links { display: none; }
  .k2-subnav__cta { margin-left: auto; }

  .k2-hero { height: 717px; }
  .k2-hero h1 { top: 80px; }
  .k2-hero__lead { top: 128px; }
  .k2-hero__wordmark { top: 257px; left: 50%; width: 429px; height: 285px; transform: translateX(-50%); }
  .k2-hero__image { top: 88px; left: calc(50% - 281px); width: 598px; height: 598px; }
  .k2-hero__detail { top: 629px; left: 106px; }

  .k2-overview { height: 1113px; }
  .k2-overview h2 { top: 56px; left: 16px; width: var(--k2-mobile-content); text-align: left; }
  .k2-overview__desktop { display: none; }
  .k2-overview__mobile { position: absolute; top: 520px; left: 16px; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px 12px; width: var(--k2-mobile-content); }
  .k2-overview__mobile article { height: 275px; padding: 30px 12px 20px; overflow: hidden; border-radius: 28px; background: #f5f5f7; }
  .k2-overview__mobile article:nth-child(n+3) { height: 270px; }
  .k2-overview__mobile h3 { min-height: 82px; font-family: 'MiSans-Bold', Arial, sans-serif; font-size: 18px; line-height: 24px; text-transform: uppercase; }
  .k2-overview__mobile p { color: rgba(0,0,0,.6); font-size: 14px; line-height: 19px; }

  .k2-composition { height: 926px; }
  .k2-section-heading { top: 56px; left: 16px; width: var(--k2-mobile-content); text-align: left; }
  .k2-section-heading p { margin-top: 24px; font-size: 14px; line-height: 19px; }
  .k2-composition__visual { top: 467px; left: 50%; width: 432px; height: 432px; transform: translateX(-50%); }
  .k2-bubble { transform: scale(.3333); transform-origin: top left; }
  .k2-bubble--bone { top: 49px; left: 103px; }
  .k2-bubble--visceral { top: 284px; left: 61px; }
  .k2-bubble--muscle { top: 179px; left: 88px; }
  .k2-bubble--protein { top: 287px; left: 201px; }
  .k2-bubble--bmr { top: 256px; left: 251px; }
  .k2-bubble--bmi { top: 131px; left: 193px; }
  .k2-bubble--weight { top: 117px; left: 289px; }
  .k2-bubble--water { top: 224px; left: 304px; }
  .k2-bubble--salts { top: 187px; left: 401px; }
  .k2-bubble--fat { top: 163px; left: 44px; }

  .k2-growth { height: 939px; }
  .k2-growth h2, .k2-height h2 { padding: 56px 16px 0; text-align: left; }
  .k2-growth .k2-section-lead, .k2-height .k2-section-lead { margin: 24px 16px 0; }
  .k2-growth img { width: var(--k2-mobile-content); height: var(--k2-mobile-content); margin-top: 71px; }

  .k2-height { height: 698px; }
  .k2-height img { width: var(--k2-mobile-content); height: 214px; margin-top: 53px; border-radius: 9px; }

  .k2-balance-agility { height: 1736px; }
  .k2-balance h2 { top: 56px; left: 16px; width: var(--k2-mobile-content); }
  .k2-balance p { position: absolute; top: 288px; left: 16px; display: block; width: var(--k2-mobile-content); font-size: 14px; line-height: 19px; }
  .k2-balance img { top: 408px; left: 50%; width: 220px; height: 418px; transform: translateX(-50%); }
  .k2-agility > img { top: 882px; left: 50%; width: 432px; height: 432px; transform: translateX(-50%); }
  .k2-agility > div { top: 1360px; left: 16px; width: var(--k2-mobile-content); }
  .k2-agility h2 { width: var(--k2-mobile-content); }
  .k2-agility p { display: block; margin-top: 24px; font-size: 14px; line-height: 19px; }

  .k2-dimensions { height: 793px; margin-top: 0; }
  .k2-dimensions h2 { top: 56px; left: 16px; width: var(--k2-mobile-content); text-align: left; }
  .k2-dimensions__visual { top: 298px; left: 50%; width: 432px; height: 495px; transform: translateX(-50%); }
  .k2-dimensions__body { width: 154px; height: 430px; }
  .k2-dimension-label { min-width: 58px; min-height: 58px; padding: 8px; font-size: 10px; }
  .k2-dimension-label--1 { top: 15px; left: 52px; }
  .k2-dimension-label--2 { top: 140px; left: 14px; }
  .k2-dimension-label--3 { top: 314px; left: 61px; }
  .k2-dimension-label--4 { top: 5px; right: 43px; }
  .k2-dimension-label--5 { top: 130px; right: 12px; }
  .k2-dimension-label--6 { top: 285px; right: 50px; }
  .k2-dimension-label--7 { top: 390px; right: 138px; }
  .k2-dimension-label--8 { top: 230px; left: 112px; }

  .k2-posture { height: 1633px; }
  .k2-posture h2 { width: var(--k2-mobile-content); padding-top: 56px; text-align: left; }
  .k2-posture .k2-section-lead { margin: 24px 16px 0; }
  .k2-posture__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; width: var(--k2-mobile-content); margin: 54px 16px 0; }
  .k2-posture__grid figure { width: 100%; height: 260px; border-radius: 18px; }

  .k2-vision { height: 697px; }
  .k2-vision h2 { width: var(--k2-mobile-content); padding-top: 56px; text-align: left; }
  .k2-vision img { width: var(--k2-mobile-content); height: 400px; margin-top: 40px; border-radius: 20px; object-fit: cover; }

  .k2-vital-capacity { height: 1301px; margin-top: -39px; }
  .k2-vital-capacity h2 { width: var(--k2-mobile-content); padding-top: 56px; text-align: left; }
  .k2-vital-capacity .k2-section-lead { margin: 24px 16px 0; }
  .k2-vital-capacity img { bottom: 0; left: -84px; width: 509px; height: 590px; }

  .k2-display { height: 636px; }
  .k2-display h2 { width: var(--k2-mobile-content); padding: 56px 16px 0; text-align: left; }
  .k2-display__metrics { justify-content: flex-start; gap: 36px; margin: 24px 16px 0; }
  .k2-display__metrics span { font-size: 11px; line-height: 15px; }
  .k2-display__metrics strong { font-size: 14px; line-height: 19px; }
  .k2-display img { width: var(--k2-mobile-content); height: 254px; margin-top: 26px; border-radius: 20px; object-fit: cover; }

  .k2-ai-vision { height: 585px; }
  .k2-ai-vision__title { top: 56px; left: 16px; }
  .k2-ai-vision__title strong { font-size: 72px; }
  .k2-ai-vision__title span { width: 190px; font-size: 24px; line-height: 32px; }
  .k2-ai-vision img { top: 0; left: 0; width: 823px; height: 585px; object-position: left top; }

  .k2-management { height: 1137px; }
  .k2-management__kicker { padding-top: 56px; font-size: 20px; line-height: 27px; }
  .k2-management h2 { width: var(--k2-mobile-content); margin-top: 8px; font-size: 32px; line-height: 42px; }
  .k2-management > .k2-canvas > img { width: var(--k2-mobile-content); height: 362px; margin-top: 24px; }
  .k2-management__cards { display: block; width: var(--k2-mobile-content); margin: 0 16px; }
  .k2-management__cards article { min-height: 245px; padding: 24px 12px 0; }
  .k2-management__cards h3 { min-height: auto; font-size: 18px; line-height: 24px; }
  .k2-management__cards p { margin-top: 20px; font-size: 13px; line-height: 18px; }

  .k2-compare-heading { height: 288px; }
  .k2-compare-heading h2 { width: var(--k2-mobile-content); padding: 103px 16px 0; font-size: 32px; line-height: 42px; text-align: left; }
  .k2-comparison { height: 1047px; }
  .k2-comparison__panel { left: auto; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0; width: var(--k2-mobile-shell); height: 1047px; margin: 0 auto; transform: none; border-radius: 0; }
  .k2-compare-product__image { width: 100%; height: auto; aspect-ratio: 1; }
  .k2-compare-product h3 { height: 56px; font-size: 24px; line-height: 32px; }
  .k2-compare-switch { top: 220px; left: calc(50% + 23px); width: 20px; height: 20px; }
  .k2-compare-data p { min-height: 25px; padding: 2px 8px; font-size: 10px; line-height: 13px; }
  .k2-compare-data hr { width: 100%; margin: 10px 0 18px; }
  .k2-compare-data strong { min-height: 46px; padding: 0 12px; font-size: 14px; line-height: 18px; }
  .k2-compare-data span { font-size: 11px; line-height: 15px; }
  .k2-compare-data .k2-frequencies { min-height: 176px; }
  .k2-compare-name--desktop { display: none; }
  .k2-compare-name--mobile { display: inline; }
  .k2-disclaimer { height: 216px; margin-top: 0; }
  .k2-disclaimer__inner { left: auto; width: var(--k2-mobile-shell); margin: 0 auto; padding: 56px 16px 0; transform: none; border-top: 0; }
  .k2-disclaimer p { font-size: 10px; line-height: 13px; }
}

/* CN uses the same K2 identity and assets, with the compact formal mobile composition. */
.k2-page--zh .k2-section h2 { text-transform: none; }
.k2-page--zh .k2-overview h2, .k2-page--zh .k2-section-heading h2 { white-space: pre-line; }
.k2-page--zh .k2-hero__image { max-width: none; }
@media (min-width: 1024px) and (max-width: 1439px) {
  .k2-page--zh .k2-canvas,
  .k2-page--zh .k2-comparison__panel,
  .k2-page--zh .k2-disclaimer__inner {
    transform: translateX(-50%) scale(var(--k2-tablet-scale));
    transform-origin: top center;
  }
}
@media (min-width: 1024px) {
  /* The formal CN desktop frame has no leading gap before dimensions, a 9px
     interval before posture, and an intentional management/compare overlap. */
  .k2-page--zh .k2-dimensions { margin-top: 0; }
  .k2-page--zh .k2-posture { margin-top: 9px; }
  .k2-page--zh .k2-compare-heading { margin-top: -140px; }
  .k2-page--zh .k2-disclaimer { margin-top: 0; }
}
@media (max-width: 1023px) {
  .k2-page--zh .k2-overview__mobile { top: 198px; width: var(--k2-mobile-content); }
  .k2-page--zh .k2-overview__mobile article { height: 92px; padding: 18px 12px; }
  .k2-page--zh .k2-overview__mobile article:nth-child(n+3) { height: 92px; }
  .k2-page--zh .k2-overview__mobile h3 { min-height: 0; font-size: 14px; line-height: 19px; }
  .k2-page--zh .k2-overview__mobile p { display: none; }
  .k2-page--zh .k2-composition { height: 662px; }
  .k2-page--zh .k2-composition__visual { top: 230px; width: min(100%, 432px); height: 432px; overflow: hidden; }
  .k2-page--zh .k2-composition__rings { width: 432px; height: 432px; }
  .k2-page--zh .k2-growth { height: 711px; }
  .k2-page--zh .k2-growth img { margin-top: 40px; }
  .k2-page--zh .k2-height { height: 489px; }
  .k2-page--zh .k2-height img { height: 180px; margin-top: 34px; }
  .k2-page--zh .k2-balance-agility { height: 1268px; }
  .k2-page--zh .k2-balance img { top: 340px; }
  .k2-page--zh .k2-agility > img { top: 720px; }
  .k2-page--zh .k2-agility > div { top: 1050px; }
  .k2-page--zh .k2-dimensions { height: 521px; }
  .k2-page--zh .k2-dimensions__visual { top: 205px; width: min(100%, 432px); height: 316px; }
  .k2-page--zh .k2-dimensions__body { height: 316px; width: 114px; }
  .k2-page--zh .k2-posture { height: 1291px; }
  .k2-page--zh .k2-posture__grid figure { height: 200px; }
  .k2-page--zh .k2-vision { height: 652px; }
  .k2-page--zh .k2-vision img { height: 355px; }
  .k2-page--zh .k2-vital-capacity { height: 614px; margin-top: 0; }
  .k2-page--zh .k2-vital-capacity img { width: 360px; height: 420px; left: 50%; transform: translateX(-50%); }
  .k2-page--zh .k2-display { height: 478px; }
  .k2-page--zh .k2-display img { height: 190px; margin-top: 20px; }
  .k2-page--zh .k2-ai-vision { height: 527px; }
  .k2-page--zh .k2-ai-vision img { height: 527px; }
  .k2-page--zh .k2-management { height: 990px; }
  .k2-page--zh .k2-management > .k2-canvas > img { height: 300px; }
  .k2-page--zh .k2-management__cards article { min-height: 200px; }
  .k2-page--zh .k2-compare-heading { height: 218px; }
  .k2-page--zh .k2-compare-heading h2 { padding-top: 68px; }
  .k2-page--zh .k2-disclaimer { height: 168px; margin-top: 0; }
  .k2-page--zh .k2-disclaimer__inner { padding-top: 36px; }
  .k2-page--zh .k2-vital-capacity img { max-width: 100%; object-fit: contain; }
}
/* Formal CN overview: six pills; the full business table stays in Compare. */
.k2-overview__cn span { position: absolute; display: flex; align-items: center; justify-content: center; padding: 8px 49px; border-radius: 66px; background: #f5f5f7; font-family: 'MiSans-Bold', Arial, sans-serif; font-size: 18px; line-height: 24px; white-space: nowrap; }
.k2-overview__cn span:nth-child(1) { top: 266px; left: 382px; }
.k2-overview__cn span:nth-child(2) { top: 266px; left: 50%; transform: translateX(-50%); }
.k2-overview__cn span:nth-child(3) { top: 266px; left: 852px; }
.k2-overview__cn span:nth-child(4) { top: 346px; left: 445px; }
.k2-overview__cn span:nth-child(5) { top: 346px; left: 50%; transform: translateX(-50%); }
.k2-overview__cn span:nth-child(6) { top: 346px; left: 825px; }
/* Reuse P-series presentation; the extra specification grows naturally. */
.k2-page--zh .k2-comparison { height: auto; }
.k2-page--zh .k2-compare-heading h2 { width: min(calc(100% - (2 * var(--compare-gutter))), 746px); padding: 0; white-space: normal; }
@media (max-width: 1023px) {
  .k2-page--zh .k2-overview h2 { font-size: clamp(38px, 10vw, 40px); }
  .k2-overview__cn span { padding: 8px 20px; }
  .k2-overview__cn span:nth-child(1) { top: 202px; left: 16px; }
  .k2-overview__cn span:nth-child(2) { top: 258px; left: 16px; transform: none; }
  .k2-overview__cn span:nth-child(3) { top: 202px; left: 184px; }
  .k2-overview__cn span:nth-child(4) { top: 314px; left: 16px; }
  .k2-overview__cn span:nth-child(5) { top: 314px; left: 166px; transform: none; }
  .k2-overview__cn span:nth-child(6) { top: 258px; left: 202px; }
  .k2-page--zh .k2-dimension-label--3 { top: 200px; }
  .k2-page--zh .k2-dimension-label--6 { top: 182px; }
  .k2-page--zh .k2-dimension-label--7 { top: 249px; }
}
</style>

<style scoped src="../../assets/styles/p-series-compare.css"></style>

<style scoped>
/* These rules affect only CN Compare, including the tablet width range. */
.k2-page--zh .k2-compare-heading { height: 253px; background: #000; }
.k2-page--zh .k2-comparison { background: #000; }
.k2-comparison__panel--cn .p5-product-card__functions li { gap: 4px; }
/* Tight product crops share one visible height inside the unchanged image boxes. */
.k2-comparison__panel--cn .p5-product-card > img { object-fit: contain; object-position: 50% 50%; padding: 10%; }
@media (max-width: 768px) {
  .k2-page--zh .k2-compare-heading { height: 218px; }
  .k2-page--zh .k2-compare-heading h2 { width: 100%; margin: 0; }
  .k2-comparison__panel--cn .p5-product-card dl > div:last-child { min-height: 90px; }
  .k2-comparison__panel--cn .p5-product-card > img { padding: min(5vw, 21.6px); }
}
</style>
